import React, { useState, useEffect } from 'react';
import {
    Table, Button, Modal, Form, Input, Select,
    Space, message, Spin, Tooltip, Tag, Popconfirm
} from 'antd';
import {
    CheckOutlined, StopOutlined, DeleteOutlined,
    PlusCircleOutlined, InfoCircleOutlined, ExpandOutlined,
    CheckCircleOutlined, FileTextOutlined
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import adminService from '../../services/adminService';

const { TextArea } = Input;

const UnansweredQuestionsPage = () => {
    const [questions, setQuestions] = useState([]);
    const [stats, setStats] = useState({ pending: 0, resolved: 0, ignored: 0, total: 0 });
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);

    // Resolve modal
    const [resolveModalVisible, setResolveModalVisible] = useState(false);
    const [resolvingQuestion, setResolvingQuestion] = useState(null);
    const [resolveForm] = Form.useForm();

    // KB'ye ekle modal
    const [kbModalVisible, setKbModalVisible] = useState(false);
    const [kbQuestion, setKbQuestion] = useState(null);
    const [kbForm] = Form.useForm();

    // Preview modal
    const [previewModalVisible, setPreviewModalVisible] = useState(false);
    const [previewContent, setPreviewContent] = useState({ title: '', content: '' });

    const fetchData = async () => {
        setLoading(true);
        try {
            const [questionsData, statsData] = await Promise.all([
                adminService.getUnansweredQuestions(statusFilter, currentPage, pageSize),
                adminService.getUnansweredStats(),
            ]);
            setQuestions(questionsData.content);
            setTotalElements(questionsData.totalElements);
            setStats(statsData);
        } catch (err) {
            message.error('Veriler yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [statusFilter, currentPage, pageSize]);

    // Resolve
    const handleResolveClick = (record) => {
        setResolvingQuestion(record);
        resolveForm.resetFields();
        setResolveModalVisible(true);
    };

    const handleResolveSubmit = async () => {
        try {
            const values = await resolveForm.validateFields();
            await adminService.resolveUnansweredQuestion(resolvingQuestion.id, values.adminNote || '');
            message.success('Soru çözüldü olarak işaretlendi');
            setResolveModalVisible(false);
            fetchData();
        } catch (err) {
            if (err.errorFields) return;
            message.error('İşlem hatası');
        }
    };

    // Ignore
    const handleIgnore = async (id) => {
        try {
            await adminService.ignoreUnansweredQuestion(id);
            message.success('Soru yok sayıldı');
            fetchData();
        } catch (err) {
            message.error('İşlem hatası');
        }
    };

    // Delete
    const handleDelete = async (id) => {
        try {
            await adminService.deleteUnansweredQuestion(id);
            message.success('Soru silindi');
            fetchData();
        } catch (err) {
            message.error('Silme hatası');
        }
    };

    // KB'ye ekle
    const handleAddToKB = (record) => {
        setKbQuestion(record);
        kbForm.resetFields();
        kbForm.setFieldsValue({
            title: record.userQuestion.length > 100
                ? record.userQuestion.substring(0, 100) + '...'
                : record.userQuestion,
            category: 'SSS',
            isActive: true,
        });
        setKbModalVisible(true);
    };

    const handleKbSubmit = async () => {
        try {
            const values = await kbForm.validateFields();
            await adminService.createKnowledgeBase(values);
            await adminService.resolveUnansweredQuestion(kbQuestion.id, 'KB\'ye eklendi');
            message.success('Bilgi kazanına eklendi ve soru çözüldü');
            setKbModalVisible(false);
            fetchData();
        } catch (err) {
            if (err.errorFields) return;
            message.error('İşlem hatası');
        }
    };

    // Preview
    const handlePreview = (title, content) => {
        setPreviewContent({ title, content });
        setPreviewModalVisible(true);
    };

    const getStatusTag = (status) => {
        switch (status) {
            case 'PENDING':
                return <Tag color="orange">Bekleyen</Tag>;
            case 'RESOLVED':
                return <Tag color="green">Çözüldü</Tag>;
            case 'IGNORED':
                return <Tag color="default">Yok Sayıldı</Tag>;
            default:
                return <Tag>{status}</Tag>;
        }
    };

    const getSourceTag = (source) => {
        switch (source) {
            case 'LLM_TAG':
                return <Tag color="blue">LLM</Tag>;
            case 'LOW_SIMILARITY':
                return <Tag color="purple">Düşük Benzerlik</Tag>;
            default:
                return <Tag>{source}</Tag>;
        }
    };

    const columns = [
        {
            title: 'Soru',
            dataIndex: 'userQuestion',
            key: 'userQuestion',
            width: 250,
            render: (text) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                        color: 'var(--admin-text-primary)',
                        fontWeight: 500,
                        maxWidth: 220,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'inline-block',
                        fontSize: 13,
                    }}>
                        {text?.substring(0, 60)}{text?.length > 60 ? '...' : ''}
                    </span>
                    {text?.length > 60 && (
                        <Tooltip title="Tam soruyu gör">
                            <Button
                                type="text"
                                size="small"
                                icon={<ExpandOutlined />}
                                onClick={() => handlePreview('Kullanıcı Sorusu', text)}
                                style={{ color: 'var(--admin-accent-primary)' }}
                            />
                        </Tooltip>
                    )}
                </div>
            ),
        },
        {
            title: 'Bot Cevabı',
            dataIndex: 'botResponse',
            key: 'botResponse',
            width: 200,
            render: (text) => (
                text ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                            color: 'var(--admin-text-secondary)',
                            maxWidth: 170,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'inline-block',
                            fontSize: 13,
                        }}>
                            {text?.substring(0, 50)}{text?.length > 50 ? '...' : ''}
                        </span>
                        {text?.length > 50 && (
                            <Tooltip title="Tam cevabı gör">
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<ExpandOutlined />}
                                    onClick={() => handlePreview('Bot Cevabı', text)}
                                    style={{ color: 'var(--admin-accent-primary)' }}
                                />
                            </Tooltip>
                        )}
                    </div>
                ) : (
                    <span style={{ color: 'var(--admin-text-tertiary)', fontStyle: 'italic', fontSize: 13 }}>
                        Bilgi bankasında bulunamadı
                    </span>
                )
            ),
        },
        {
            title: 'Kaynak',
            dataIndex: 'detectionSource',
            key: 'detectionSource',
            width: 110,
            render: (source) => getSourceTag(source),
        },
        {
            title: 'Durum',
            dataIndex: 'status',
            key: 'status',
            width: 110,
            render: (status) => getStatusTag(status),
        },
        {
            title: 'Tarih',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 150,
            render: (date) => (
                date ? (
                    <span style={{ color: 'var(--admin-text-tertiary)', fontSize: 12 }}>
                        {new Date(date).toLocaleString('tr-TR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                        })}
                    </span>
                ) : '-'
            ),
        },
        {
            title: 'İşlemler',
            key: 'actions',
            fixed: 'right',
            width: 200,
            render: (_, record) => (
                <Space size={4}>
                    {record.status === 'PENDING' && (
                        <>
                            <Tooltip title="Çözüldü olarak işaretle">
                                <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                                    <Button
                                        className="admin-btn-icon"
                                        icon={<CheckOutlined />}
                                        onClick={() => handleResolveClick(record)}
                                        style={{ color: '#22c55e' }}
                                    />
                                </motion.div>
                            </Tooltip>
                            <Tooltip title="Yok say">
                                <Popconfirm
                                    title="Bu soruyu yok saymak istediğinize emin misiniz?"
                                    onConfirm={() => handleIgnore(record.id)}
                                    okText="Evet"
                                    cancelText="İptal"
                                >
                                    <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                                        <Button
                                            className="admin-btn-icon"
                                            icon={<StopOutlined />}
                                        />
                                    </motion.div>
                                </Popconfirm>
                            </Tooltip>
                            <Tooltip title="Bilgi Kazanına Ekle">
                                <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                                    <Button
                                        className="admin-btn-icon"
                                        icon={<PlusCircleOutlined />}
                                        onClick={() => handleAddToKB(record)}
                                        style={{ color: '#6366f1' }}
                                    />
                                </motion.div>
                            </Tooltip>
                        </>
                    )}
                    <Tooltip title="Sil">
                        <Popconfirm
                            title="Bu kaydı silmek istediğinize emin misiniz?"
                            onConfirm={() => handleDelete(record.id)}
                            okText="Sil"
                            cancelText="İptal"
                        >
                            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                                <Button
                                    className="admin-btn-icon"
                                    icon={<DeleteOutlined />}
                                    danger
                                />
                            </motion.div>
                        </Popconfirm>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div>
            {/* Page Header */}
            <div className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">Cevaplanamayan Sorular</h1>
                    <p className="admin-page-subtitle">
                        Chatbot'un cevaplayamadığı veya temsilciye yönlendirdiği soruları yönetin.
                    </p>
                </div>
            </div>

            {/* Stats Cards */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
                <motion.div
                    whileHover={{ scale: 1.02 }}
                    style={{
                        flex: 1,
                        background: 'rgba(249, 115, 22, 0.1)',
                        border: '1px solid rgba(249, 115, 22, 0.2)',
                        borderRadius: 8,
                        padding: '16px 20px',
                    }}
                >
                    <div style={{ color: 'var(--admin-text-tertiary)', fontSize: 13 }}>Bekleyen</div>
                    <div style={{ color: '#f97316', fontSize: 28, fontWeight: 700, marginTop: 4 }}>
                        {stats.pending}
                    </div>
                </motion.div>
                <motion.div
                    whileHover={{ scale: 1.02 }}
                    style={{
                        flex: 1,
                        background: 'rgba(34, 197, 94, 0.1)',
                        border: '1px solid rgba(34, 197, 94, 0.2)',
                        borderRadius: 8,
                        padding: '16px 20px',
                    }}
                >
                    <div style={{ color: 'var(--admin-text-tertiary)', fontSize: 13 }}>Çözülen</div>
                    <div style={{ color: '#22c55e', fontSize: 28, fontWeight: 700, marginTop: 4 }}>
                        {stats.resolved}
                    </div>
                </motion.div>
                <motion.div
                    whileHover={{ scale: 1.02 }}
                    style={{
                        flex: 1,
                        background: 'rgba(148, 163, 184, 0.1)',
                        border: '1px solid rgba(148, 163, 184, 0.2)',
                        borderRadius: 8,
                        padding: '16px 20px',
                    }}
                >
                    <div style={{ color: 'var(--admin-text-tertiary)', fontSize: 13 }}>Yok Sayılan</div>
                    <div style={{ color: '#94a3b8', fontSize: 28, fontWeight: 700, marginTop: 4 }}>
                        {stats.ignored}
                    </div>
                </motion.div>
            </div>

            {/* Info Banner */}
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                    background: 'rgba(99, 102, 241, 0.1)',
                    border: '1px solid rgba(99, 102, 241, 0.2)',
                    borderRadius: 8,
                    padding: '12px 16px',
                    marginBottom: 20,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                }}
            >
                <InfoCircleOutlined style={{ color: '#6366f1', marginTop: 2 }} />
                <div style={{ fontSize: 13, color: 'var(--admin-text-secondary)', lineHeight: 1.6 }}>
                    <strong style={{ color: 'var(--admin-text-primary)' }}>Nasıl Çalışır:</strong><br />
                    Chatbot cevaplayamadığında veya kullanıcı temsilciye yönlendirildiğinde sorular
                    otomatik olarak burada listelenir. <strong>"KB'ye Ekle"</strong> butonu ile soruyu
                    doğrudan bilgi kazanına ekleyebilirsiniz.
                </div>
            </motion.div>

            {/* Status Filter */}
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--admin-text-tertiary)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: 4 }}>Filtre</span>
                {[
                    { key: null, label: 'Tümü', count: stats.total, color: '#818cf8', bg: 'rgba(99, 102, 241, 0.15)', border: 'rgba(99, 102, 241, 0.5)' },
                    { key: 'PENDING', label: 'Bekleyen', count: stats.pending, color: '#fbbf24', bg: 'rgba(245, 158, 11, 0.15)', border: 'rgba(245, 158, 11, 0.5)' },
                    { key: 'RESOLVED', label: 'Çözülen', count: stats.resolved, color: '#34d399', bg: 'rgba(16, 185, 129, 0.15)', border: 'rgba(16, 185, 129, 0.5)' },
                    { key: 'IGNORED', label: 'Yok Sayılan', count: stats.ignored, color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.15)', border: 'rgba(148, 163, 184, 0.5)' },
                ].map((item) => {
                    const isActive = statusFilter === item.key;
                    return (
                        <button
                            key={item.key || 'all'}
                            onClick={() => { setStatusFilter(isActive && item.key !== null ? null : item.key); setCurrentPage(0); }}
                            style={{
                                padding: '6px 14px',
                                borderRadius: 8,
                                border: `1px solid ${isActive ? item.border : 'var(--admin-border-color)'}`,
                                background: isActive ? item.bg : 'var(--admin-glass-bg)',
                                color: isActive ? item.color : 'var(--admin-text-secondary)',
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                fontFamily: 'inherit',
                            }}
                        >
                            {item.label} <span style={{ opacity: 0.7, marginLeft: 2 }}>{item.count}</span>
                        </button>
                    );
                })}
            </div>

            {/* Table */}
            <motion.div
                className="admin-table-container"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
            >
                {loading ? (
                    <div className="admin-loading">
                        <Spin size="large" />
                    </div>
                ) : (
                    <Table
                        scroll={{ x: 'max-content' }}
                        columns={columns}
                        dataSource={questions}
                        rowKey="id"
                        pagination={{
                            current: currentPage + 1,
                            pageSize: pageSize,
                            total: totalElements,
                            onChange: (page, size) => {
                                setCurrentPage(page - 1);
                                setPageSize(size);
                            },
                            showSizeChanger: true,
                            showTotal: (total) => `Toplam ${total} kayıt`,
                        }}
                    />
                )}
            </motion.div>

            {/* Resolve Modal */}
            <Modal
                title={
                    <div className="admin-modal-heading">
                        <div className="admin-modal-heading-icon">
                            <CheckCircleOutlined />
                        </div>
                        <div>
                            <div className="admin-modal-heading-title">Soruyu Çözüldü Olarak İşaretle</div>
                            <div className="admin-modal-heading-subtitle">
                                Soruyu kapatın, dilerseniz çözüme dair bir not ekleyin
                            </div>
                        </div>
                    </div>
                }
                open={resolveModalVisible}
                onOk={handleResolveSubmit}
                onCancel={() => setResolveModalVisible(false)}
                okText="Çözüldü"
                cancelText="İptal"
                className="admin-modal"
                rootClassName="admin-modal"
                okButtonProps={{ className: 'admin-btn-primary' }}
                cancelButtonProps={{ className: 'admin-btn-secondary' }}
            >
                {resolvingQuestion && (
                    <div style={{
                        background: 'var(--admin-bg-tertiary)',
                        borderRadius: 8,
                        padding: 12,
                        marginBottom: 16,
                        fontSize: 13,
                        color: 'var(--admin-text-secondary)',
                    }}>
                        <strong style={{ color: 'var(--admin-text-primary)' }}>Soru:</strong><br />
                        {resolvingQuestion.userQuestion}
                    </div>
                )}
                <Form form={resolveForm} layout="vertical">
                    <Form.Item
                        name="adminNote"
                        label="Admin Notu (opsiyonel)"
                    >
                        <TextArea
                            rows={3}
                            placeholder="Nasıl çözüldü? Örnek: KB'ye vize bilgisi eklendi"
                        />
                    </Form.Item>
                </Form>
            </Modal>

            {/* KB'ye Ekle Modal */}
            <Modal
                title={
                    <div className="admin-modal-heading">
                        <div className="admin-modal-heading-icon">
                            <PlusCircleOutlined />
                        </div>
                        <div>
                            <div className="admin-modal-heading-title">Bilgi Kazanına Ekle</div>
                            <div className="admin-modal-heading-subtitle">
                                Sorunun cevabını bilgi kazanına ekleyin ve soruyu çözün
                            </div>
                        </div>
                    </div>
                }
                open={kbModalVisible}
                onOk={handleKbSubmit}
                onCancel={() => setKbModalVisible(false)}
                okText="Ekle ve Çöz"
                cancelText="İptal"
                width="92vw"
                style={{ maxWidth: 700 }}
                className="admin-modal"
                rootClassName="admin-modal"
                okButtonProps={{ className: 'admin-btn-primary' }}
                cancelButtonProps={{ className: 'admin-btn-secondary' }}
            >
                {kbQuestion && (
                    <div style={{
                        background: 'var(--admin-bg-tertiary)',
                        borderRadius: 8,
                        padding: 12,
                        marginBottom: 16,
                        fontSize: 13,
                        color: 'var(--admin-text-secondary)',
                    }}>
                        <strong style={{ color: 'var(--admin-text-primary)' }}>Kullanıcının Sorusu:</strong><br />
                        {kbQuestion.userQuestion}
                    </div>
                )}
                <Form form={kbForm} layout="vertical">
                    <Form.Item
                        name="title"
                        label="Başlık"
                        rules={[{ required: true, message: 'Başlık gerekli' }]}
                    >
                        <Input placeholder="Örnek: Vize Başvuru Süreci" />
                    </Form.Item>

                    <Form.Item
                        name="category"
                        label="Kategori"
                        rules={[{ required: true, message: 'Kategori gerekli' }]}
                    >
                        <Select
                            placeholder="Kategori seçin"
                            options={[
                                { label: 'SSS', value: 'SSS' },
                                { label: 'Politikalar', value: 'Politikalar' },
                                { label: 'Kartlar', value: 'Kartlar' },
                                { label: 'Hizmetler', value: 'Hizmetler' },
                            ]}
                            showSearch
                            allowClear
                        />
                    </Form.Item>

                    <Form.Item
                        name="content"
                        label="Cevap İçeriği"
                        rules={[{ required: true, message: 'İçerik gerekli' }]}
                        extra="Bu içerik chatbot'un bilgi kazanına eklenecek. Sorunun cevabını yazın."
                    >
                        <TextArea
                            rows={6}
                            placeholder="Kullanıcının sorusuna verilecek doğru cevabı buraya yazın..."
                            style={{ fontSize: 13, lineHeight: 1.5 }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="isActive"
                        hidden
                        initialValue={true}
                    >
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Preview Modal */}
            <Modal
                title={
                    <div className="admin-modal-heading">
                        <div className="admin-modal-heading-icon">
                            <FileTextOutlined />
                        </div>
                        <div>
                            <div className="admin-modal-heading-title">{previewContent.title}</div>
                            <div className="admin-modal-heading-subtitle">
                                Metnin tamamını görüntüleyin
                            </div>
                        </div>
                    </div>
                }
                open={previewModalVisible}
                onCancel={() => setPreviewModalVisible(false)}
                footer={null}
                width="92vw"
                style={{ maxWidth: 600 }}
                className="admin-modal"
                rootClassName="admin-modal"
            >
                <div style={{
                    background: 'var(--admin-bg-tertiary)',
                    borderRadius: 8,
                    padding: 20,
                    maxHeight: 400,
                    overflowY: 'auto',
                    whiteSpace: 'pre-wrap',
                    fontSize: 14,
                    lineHeight: 1.7,
                    color: 'var(--admin-text-secondary)',
                }}>
                    {previewContent.content}
                </div>
            </Modal>
        </div>
    );
};

export default UnansweredQuestionsPage;
